import { useCallback } from 'react';
import PropTypes from 'prop-types';
import { cx, noop } from 'ramda-extension';

import Text from '@gef-ui/components/atoms/Text';

const Pagination = ({ page, size, totalRows, onPaginationClick }) => {
	const handlePaginationClick = useCallback(
		(toGo) => () => {
			onPaginationClick(toGo - 1);
		},
		[onPaginationClick],
	);

	const renderPaginationItem = ({ page, isLink, withSpace }, key) => {
		if (page) {
			return (
				<Text
					key={key}
					type={isLink ? Text.typeConstants.PRIMARY : Text.typeConstants.DISABLED}
					onClick={isLink ? handlePaginationClick(page) : noop}
				>
					{withSpace ? (
						<span className={cx({ Link: isLink, Zoom: isLink })} id="item">
							&nbsp;&nbsp;{page}
						</span>
					) : (
						<span className={cx({ Link: isLink, Zoom: isLink })} id="item">
							{page}
						</span>
					)}
				</Text>
			);
		} else {
			return (
				<span className="Link" key={key}>
					...
				</span>
			);
		}
	};

	const createPagination = (page, size, totalRows) => {
		const lastPage = Math.ceil(totalRows / size);
		const pagination = [];
		const empty = {};
		if (page > 3 && lastPage >= 7) {
			pagination.push({ page: 1, isLink: true, withSpace: true });
			pagination.push(empty);
		}

		if (lastPage < 7) {
			// render pagination with small number of pages
			for (let i = 1; i < lastPage + 1; i++) {
				pagination.push({ page: i, isLink: i !== page, withSpace: true });
			}
		} else if (page < 3) {
			// i am at beginning
			for (let i = 1; i < 6; i++) {
				pagination.push({ page: i, isLink: i !== page, withSpace: true });
			}
		} else if (page >= lastPage - 2) {
			// i am at end
			for (let i = lastPage - 4; i <= lastPage; i++) {
				pagination.push({ page: i, isLink: i !== page, withSpace: i !== lastPage - 4 });
			}
		} else {
			for (let i = page - 2; i <= page + 2; i++) {
				pagination.push({ page: i, isLink: i !== page, withSpace: i !== page - 2 || i === 2 });
			}
		}

		if (page < lastPage - 2 && lastPage > 6) {
			const removeThreeDot = page === lastPage - 3; // remove dots on third last page
			removeThreeDot || pagination.push(empty);
			pagination.push({ page: lastPage, isLink: true, withSpace: removeThreeDot });
		}

		if (pagination.length >= 3 && pagination[0].page === 1 && pagination[1] === empty && pagination[2].page === 2) {
			return pagination.filter((item, index) => index !== 1);
		}

		return pagination;
	};

	return createPagination(page, size, totalRows).map(renderPaginationItem);
};

Pagination.propTypes = {
	onPaginationClick: PropTypes.func,
	page: PropTypes.number,
	size: PropTypes.number,
	totalRows: PropTypes.number,
};

export default Pagination;
